import styled from 'styled-components';

import Box from '../../components/Box';
import StyledChip from '../../components/Chip';
import ContentHeaderStyled from '../../components/ContentHeader';
import Flex from '../../components/Flex';
import PaginationStyled from '../../components/Pagination';
import { LIBRARY_LINKS } from '../../constants';
import { Button, IconDownload as DownloadIcon, IconEye as EyeIcon } from '../../core';
import { useDownloadFile } from '../../hooks/useDownloadFile';
import { ReactComponent as DashboardIcon } from '../../images/icons/dashboard.svg';
import { ReactComponent as PowerBiIcon } from '../../images/icons/power-bi.svg';
import { ReactComponent as StoryIcon } from '../../images/icons/storytelling.svg';

import bhDashboard from './images/bh-dashboard.webp';
import calAimTemplateImage from './images/calaim-template.webp';
import covid19Image from './images/covid19.webp';
import ltsRelationalImage from './images/ltss-relational.webp';
import templateTitleCalaimImage from './images/template-title-calaim.webp';

interface Props {
  className?: string;
}

const DashboardExamples = ({ className = '' }: Props) => {
  const downloadFile = useDownloadFile();

  return (
    <div className={className}>
      <ContentHeaderStyled
        title="Dashboard Examples"
        breadcrumbs={[{ label: 'Design System', url: '/' }, { label: 'Dashboard Examples' }]}
        className="hero-section"
      />
      <Box as="section" className="example-section">
        <Flex justify="space-between">
          <Box>
            <h2>BH Dashboard</h2>
            <Flex gap="16px">
              <StyledChip size="S" icon={<PowerBiIcon />}>
                Power BI
              </StyledChip>
              <StyledChip size="S" icon={<DashboardIcon />}>
                Dashboard
              </StyledChip>
            </Flex>
          </Box>

          <Flex gap="16px">
            <Button
              iconAfter={<EyeIcon />}
              onClick={() =>
                window.open(
                  'https://behavioralhealth-data.dhcs.ca.gov/pages/california-behavioral-health-plan-dashboard~282cc688e8264a93b999b607f46d7268',
                  '_blank',
                )
              }
            >
              Open Dashboard
            </Button>
          </Flex>
        </Flex>
        <p>
          This dashboard provides a high-level overview of behavioral health network adequacy and
          regulatory compliance for Medi-Cal managed care programs in California. It enables
          stakeholders to evaluate whether service networks meet established capacity, access, and
          compliance standards across counties, supporting oversight, planning, and accountability
          in the delivery of behavioral health services.
        </p>

        <Box className="single-img-container" style={{ marginTop: '24px' }}>
          <img src={bhDashboard} alt="img" />
        </Box>

        <hr />
      </Box>

      <Box as="section" style={{ marginTop: '40px' }} className="example-section">
        <Flex justify="space-between">
          <Box>
            <h2>CalAIM Dashboard</h2>
            <Flex gap="16px">
              <StyledChip size="S" icon={<PowerBiIcon />}>
                Power BI
              </StyledChip>
              <StyledChip size="S" icon={<StoryIcon />}>
                Storytelling
              </StyledChip>
            </Flex>
          </Box>

          <Flex gap="16px">
            <Button
              iconAfter={<EyeIcon />}
              onClick={() =>
                window.open('https://calaim.dhcs.ca.gov/pages/behavioral-health', '_blank')
              }
            >
              Open Dashboard
            </Button>
          </Flex>
        </Flex>
        <p>
          The CalAIM Dashboard Storytelling Template provides a structured approach to presenting
          data insights effectively. Incorporating strong UI design principles, including background
          contrast, enhances readability and user experience. Below are detailed instructions with
          an emphasis on background contrast and other UI considerations:{' '}
        </p>

        <Flex className="two-img-container" style={{ marginTop: '24px' }} gap="16px">
          <img src={calAimTemplateImage} alt="img" />

          <img src={templateTitleCalaimImage} alt="img" />
        </Flex>

        <hr />
      </Box>

      <Box as="section" style={{ marginTop: '40px' }} className="example-section">
        <Flex justify="space-between">
          <Box>
            <h2>LTSS Relational</h2>
            <Flex gap="16px">
              <StyledChip size="S" icon={<PowerBiIcon />}>
                Power BI
              </StyledChip>
              <StyledChip size="S" icon={<DashboardIcon />}>
                Dashboard
              </StyledChip>
            </Flex>
          </Box>

          <Flex gap="16px">
            <Button
              iconAfter={<EyeIcon />}
              onClick={() =>
                window.open(
                  'https://www.dhcs.ca.gov/dataandstats/dashboards/Pages/LTSS-Dashboard.aspx',
                  '_blank',
                )
              }
            >
              Open Dashboard
            </Button>
          </Flex>
        </Flex>
        <p>
          The DHCS LTSS Relational Dashboard is an intuitive dashboard template developed
          specifically for the California Department of Health Care Services (DHCS). It is designed
          to facilitate the management and analysis of Long-Term Services and Supports (LTSS) data,
          providing a centralized and interactive platform to visualize key metrics, monitor
          performance, and support data-driven decision-making.{' '}
        </p>

        <Box className="single-img-container" style={{ marginTop: '24px' }}>
          <img src={ltsRelationalImage} alt="img" />
        </Box>

        <hr />
      </Box>

      <Box as="section" style={{ marginTop: '40px' }} className="example-section">
        <Flex justify="space-between">
          <Box>
            <h2>Covid 19 Vaccination - Linked Services</h2>
            <Flex gap="16px">
              <StyledChip size="S" icon={<PowerBiIcon />}>
                Power BI
              </StyledChip>
              <StyledChip size="S" icon={<DashboardIcon />}>
                Dashboard
              </StyledChip>
            </Flex>
          </Box>

          <Flex gap="16px">
            <Button
              iconAfter={<DownloadIcon />}
              onPointerDown={() =>
                downloadFile(
                  'COVID-19 Vaccination Dashboard.pbix',
                  LIBRARY_LINKS.covid19_dashboard_path,
                )
              }
            >
              Download Dashboard
            </Button>
          </Flex>
        </Flex>
        <p>
          Developed for the California Department of Health Care Services (DHCS), the COVID-19
          Vaccination Dashboard is a dynamic tool for tracking and analyzing vaccination data across
          the state. It offers a centralized platform to monitor vaccination rates, demographic
          trends, and distribution efforts, helping stakeholders assess progress and identify gaps.
          With interactive visualizations, the dashboard supports data-driven decision-making to
          enhance public health strategies.
        </p>

        <Box className="single-img-container" style={{ marginTop: '24px' }}>
          <img src={covid19Image} alt="img" />
        </Box>
      </Box>

      <PaginationStyled
        previousLink="/design-system/chart-match"
        previous="Chart Match"
        nextLink="/dev-resources"
        next="Dev Resources"
      />
    </div>
  );
};
const StyledDashboardExamples = styled(DashboardExamples)`
  width: 100%;

  .hero-section {
    margin-bottom: 54px;
  }

  .example-section {
    h2 {
      color: ${({ theme }) => theme.neutralText};
      font-size: ${({ theme }) => theme.fontSizes.xl};
      line-height: ${({ theme }) => theme.fontSizes.xl};
      font-weight: 300;
      margin-bottom: 16px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: 20px;
      font-weight: 400;
      margin-top: 24px;
      max-width: 700px;
    }

    .two-img-container,
    .single-img-container {
      img {
        box-shadow: 0 2px 4px 0 #141c2c0f; /* tokens-allow: pending design decision */
        width: 100%;
      }
    }

    .two-img-container {
      img {
        max-height: 312px;
      }
    }

    .single-img-container {
      img {
        max-height: 556px;
      }
    }

    hr {
      border: 1px dashed ${({ theme }) => theme.neutralBorder};
      margin-top: 24px;
    }
  }
`;

export default StyledDashboardExamples;
